import type { CSSProperties } from 'react'

import * as stylex from '@stylexjs/stylex'
import Icon, { type IconName } from './Icon'

type AvatarSubject = {
  name: string
  color?: number
  avatar?: string
}

type AvatarProps = {
  person: AvatarSubject
  size?: number
  className?: string
  style?: CSSProperties
}

type AvatarPalette = {
  background: string
  hair: string
  shirt: string
  skin: string
}

const styles = stylex.create({
  root: {
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: '9999px',
    backgroundColor: '#e8efe6',
    fontSize: '0.76em',
    fontWeight: 600,
    color: '#365846',
    boxShadow: '0 0 0 1px rgb(0 0 0 / 5%)',
  },
  fullSize: {
    width: '100%',
    height: '100%',
  },
  screenReaderOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
})

const palettes: AvatarPalette[] = [
  { background: '#e4eee7', hair: '#33241f', shirt: '#4b7665', skin: '#e7a37f' },
  { background: '#e4edf2', hair: '#20211e', shirt: '#3e627a', skin: '#bf7c52' },
  { background: '#f0e9e2', hair: '#28211e', shirt: '#806b5c', skin: '#b96e50' },
  { background: '#f0e6e0', hair: '#3b2722', shirt: '#7d5542', skin: '#d89d7a' },
  { background: '#e5eee9', hair: '#252f2d', shirt: '#4c7a68', skin: '#c68d68' },
  { background: '#ede7ef', hair: '#302231', shirt: '#755d7f', skin: '#d29a78' },
  { background: '#f1ecdf', hair: '#3a2b20', shirt: '#a27a43', skin: '#e0a47d' },
  { background: '#e6ebef', hair: '#242a31', shirt: '#526e86', skin: '#c78663' },
  { background: '#e9eee3', hair: '#363021', shirt: '#6e7a4f', skin: '#d69a70' },
  { background: '#f0e5e6', hair: '#352127', shirt: '#8e5c68', skin: '#e2a582' },
  // The FNV-1a seed for "Patrick Everon" resolves to this palette slot.
  // Ginger is therefore a normal algorithmic result, rather than a name
  // specific exception.
  { background: '#f2e6dc', hair: '#bd5f32', shirt: '#55796d', skin: '#e6a178' },
  { background: '#eee7df', hair: '#36251e', shirt: '#9a684a', skin: '#d4936c' },
]

function normalizeName(name: string) {
  return name.trim().toLocaleLowerCase().replace(/\s+/g, ' ')
}

function nameSeed(name: string) {
  // FNV-1a gives a stable, well-distributed value without relying on the
  // backend's persisted color index. The same name therefore looks the same
  // in every list, story, and environment.
  let hash = 2166136261
  for (const character of normalizeName(name)) {
    hash ^= character.codePointAt(0) ?? 0
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function paletteFor(name: string) {
  return palettes[nameSeed(name) % palettes.length]
}

const avatarIconNames = new Set<IconName>([
  'people',
  'food',
  'travel',
  'shopping',
  'gift',
  'receipt',
])

export default function Avatar({ person, size = 40, className = '', style }: AvatarProps) {
  const palette = paletteFor(person.name)
  const avatarImage = person.avatar?.startsWith('data:image/') || person.avatar?.startsWith('https://')
  const initials = person.name
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)

  const rootProps = stylex.props(styles.root)
  const avatarIcon = person.avatar && avatarIconNames.has(person.avatar as IconName)

  return (
    <span
      aria-label={person.name}
      className={`${rootProps.className} ${className}`.trim()}
      style={{ height: size, width: size, ...style }}
      title={person.name}
    >
      {avatarImage ? (
        <img alt="" draggable="false" src={person.avatar} style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
      ) : avatarIcon ? (
        <Icon
          className=""
          name={person.avatar as IconName}
          style={{ height: size * 0.48, width: size * 0.48 }}
        />
      ) : (
        <svg aria-hidden="true" {...stylex.props(styles.fullSize)} viewBox="0 0 48 48">
          <rect fill={palette.background} height="48" width="48" />
          <path d="M7 48c1-12 8-17 17-17s16 6 17 17" fill={palette.shirt} />
          <circle cx="24" cy="20" fill={palette.skin} r="11" />
          <path d="M13 20c-1-10 4-15 12-15 8 0 12 6 10 16-2-2-4-6-5-9-4 4-10 6-17 5Z" fill={palette.hair} />
          <circle cx="20" cy="21" fill="#35231d" r="1" /><circle cx="28" cy="21" fill="#35231d" r="1" />
          <path d="M20 26c2 1.5 6 1.5 8 0" fill="none" stroke="#784838" strokeLinecap="round" strokeWidth="1" />
        </svg>
      )}
      <span {...stylex.props(styles.screenReaderOnly)}>{initials}</span>
    </span>
  )
}
